import React, { useEffect, useMemo, useState } from 'react';
import { Table, Button, Modal, Select, Input, Tag, Tooltip, Popconfirm, message } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, WarningOutlined } from '@ant-design/icons';
import { useOneOnOneActions } from '_actions/oneonone.actions';
import { useFetchWrapper } from '_helpers';
import { ROLE_GROUPS } from '_helpers/roles';
import DatePicker from '_helpers/DatePicker';
import dayjs from '_helpers/datetime';

export { OneOnOnePage };

// dayjs().day(): 0=Sun..6=Sat → Vietnamese weekday label.
const WEEKDAY_VN = ['CN', 'Thứ 2', 'Thứ 3', 'Thứ 4', 'Thứ 5', 'Thứ 6', 'Thứ 7'];
const thuOf = (date) => (date ? WEEKDAY_VN[dayjs(date).day()] : '—');

const blankForm = {
    open: false, id: null,
    student_ref: null, teacher_ref: null,
    date: null, time_in: '', time_out: '', content: '', notes: '',
};

function OneOnOnePage() {
    const actions = useOneOnOneActions();
    const fetchWrapper = useFetchWrapper();
    const userData = useMemo(() => JSON.parse(localStorage.getItem('userData') || 'null'), []);
    const isStaff = ROLE_GROUPS.STAFF.includes(userData?.role);

    const [records, setRecords] = useState([]);
    const [overExcused, setOverExcused] = useState([]);
    const [students, setStudents] = useState([]);
    const [teachers, setTeachers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [form, setForm] = useState(blankForm);

    async function load() {
        setLoading(true);
        const data = await actions.getAll();
        setRecords(Array.isArray(data?.records) ? data.records : []);
        setOverExcused(Array.isArray(data?.overExcused) ? data.overExcused : []);
        setLoading(false);
    }

    async function loadPickers() {
        if (!isStaff) return;
        try {
            const [sRes, tRes] = await Promise.all([
                fetchWrapper.get('/api/manager/students', null, null),
                fetchWrapper.get('/api/manager/teachers', null, null),
            ]);
            const sJson = await sRes.json();
            const tJson = await tRes.json();
            if (sJson.status === 'Success') setStudents(Array.isArray(sJson.message) ? sJson.message : []);
            if (tJson.status === 'Success') setTeachers(Array.isArray(tJson.message) ? tJson.message : []);
        } catch (_) { /* picker lists are best-effort */ }
    }

    useEffect(() => { load(); loadPickers(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const overSet = useMemo(() => new Set(overExcused.map(String)), [overExcused]);

    function openAdd() { setForm({ ...blankForm, open: true }); }
    function openEdit(r) {
        setForm({
            open: true, id: r._id,
            student_ref: r.student_ref?._id || null,
            teacher_ref: r.teacher_ref?._id || null,
            date: r.date ? dayjs(r.date) : null,
            time_in: r.time_in || '', time_out: r.time_out || '',
            content: r.content || '', notes: r.notes || '',
        });
    }

    async function save() {
        if (!form.student_ref) { message.warning('Chọn học sinh'); return; }
        setSaving(true);
        const payload = {
            student_ref: form.student_ref,
            teacher_ref: form.teacher_ref || null,
            date: form.date ? form.date.toISOString() : null,
            time_in: form.time_in, time_out: form.time_out,
            content: form.content, notes: form.notes,
        };
        try {
            const res = form.id ? await actions.update(form.id, payload) : await actions.create(payload);
            if (res.status === 'Success') {
                message.success(form.id ? 'Đã lưu' : 'Đã thêm buổi 1:1');
                setForm(blankForm);
                await load();
            } else {
                message.error(typeof res.message === 'string' ? res.message : 'Có lỗi xảy ra');
            }
        } catch (_) { message.error('Có lỗi xảy ra'); }
        setSaving(false);
    }

    async function del(id) {
        const res = await actions.remove(id);
        if (res.status === 'Success') { message.success('Đã xoá'); await load(); }
        else message.error('Không xoá được');
    }

    const columns = [
        { title: 'Thứ', dataIndex: 'date', width: 70, render: d => thuOf(d) },
        { title: 'Ngày', dataIndex: 'date', width: 110, render: d => (d ? dayjs(d).format('DD/MM/YYYY') : '—') },
        {
            title: 'Học sinh', dataIndex: 'student_ref', width: 200,
            render: s => (
                <span>
                    {s?.name || s?.student_id || '—'}
                    {s?._id && overSet.has(String(s._id)) &&
                        <Tooltip title="Vượt 10% số buổi vắng có phép trong một lớp">
                            <Tag color="warning" style={{ marginLeft: 6 }}><WarningOutlined /> &gt;10%</Tag>
                        </Tooltip>}
                </span>
            ),
        },
        { title: 'Giờ vào', dataIndex: 'time_in', width: 80, render: v => v || '—' },
        { title: 'Giờ ra', dataIndex: 'time_out', width: 80, render: v => v || '—' },
        {
            title: 'GV phụ trách', dataIndex: 'teacher_ref', width: 160,
            render: t => (t ? (t.name || t.student_id) : <Tag>Chưa phân công</Tag>),
        },
        { title: 'Nội dung học', dataIndex: 'content', render: v => v || '—' },
        { title: 'Ghi chú', dataIndex: 'notes', render: v => v || '—' },
    ];
    if (isStaff) {
        columns.push({
            title: '', key: 'actions', width: 90, fixed: 'right',
            render: (_, r) => (
                <span style={{ whiteSpace: 'nowrap' }}>
                    <Button size="small" type="text" icon={<EditOutlined />} onClick={() => openEdit(r)} />
                    <Popconfirm title="Xoá buổi 1:1 này?" okText="Xoá" cancelText="Hủy" onConfirm={() => del(r._id)}>
                        <Button size="small" type="text" danger icon={<DeleteOutlined />} />
                    </Popconfirm>
                </span>
            ),
        });
    }

    return (
        <div className="att2-page soft-in">
            <div className="att2-head">
                <div className="att2-head-l">
                    <h1 className="att2-title">Lớp 1:1 (học bù)</h1>
                </div>
                {isStaff &&
                    <div className="att2-tools">
                        <Button type="primary" icon={<PlusOutlined />} onClick={openAdd}>Thêm buổi 1:1</Button>
                    </div>}
            </div>

            <div className="float-panel soft-in-scale">
                <Table
                    rowKey="_id"
                    size="small"
                    loading={loading}
                    dataSource={records}
                    columns={columns}
                    pagination={{ pageSize: 20, hideOnSinglePage: true }}
                    scroll={{ x: 'max-content' }}
                />
            </div>

            <Modal
                title={form.id ? 'Sửa buổi 1:1' : 'Thêm buổi 1:1'}
                visible={form.open}
                onCancel={() => setForm(blankForm)}
                onOk={save}
                confirmLoading={saving}
                okText={form.id ? 'Lưu' : 'Thêm'} cancelText="Hủy"
            >
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    <div>
                        <label>Học sinh *</label>
                        <Select
                            style={{ width: '100%' }} placeholder="Chọn học sinh" showSearch optionFilterProp="label"
                            value={form.student_ref} onChange={v => setForm(f => ({ ...f, student_ref: v }))}
                            options={students.map(s => ({ value: s._id, label: `${s.name || ''} (${s.student_id})` }))}
                        />
                    </div>
                    <div>
                        <label>Giáo viên phụ trách</label>
                        <Select
                            style={{ width: '100%' }} placeholder="Chưa phân công" allowClear showSearch optionFilterProp="label"
                            value={form.teacher_ref} onChange={v => setForm(f => ({ ...f, teacher_ref: v || null }))}
                            options={teachers.map(t => ({ value: t._id, label: `${t.name || ''} (${t.student_id})` }))}
                        />
                    </div>
                    <div>
                        <label>Ngày {form.date && `(${thuOf(form.date)})`}</label>
                        <DatePicker
                            style={{ width: '100%' }} format="DD/MM/YYYY"
                            value={form.date} onChange={v => setForm(f => ({ ...f, date: v }))}
                        />
                    </div>
                    <div style={{ display: 'flex', gap: 12 }}>
                        <div style={{ flex: 1 }}>
                            <label>Giờ vào</label>
                            <Input type="time" value={form.time_in} onChange={e => setForm(f => ({ ...f, time_in: e.target.value }))} />
                        </div>
                        <div style={{ flex: 1 }}>
                            <label>Giờ ra</label>
                            <Input type="time" value={form.time_out} onChange={e => setForm(f => ({ ...f, time_out: e.target.value }))} />
                        </div>
                    </div>
                    <div>
                        <label>Nội dung học</label>
                        <Input.TextArea rows={2} value={form.content} onChange={e => setForm(f => ({ ...f, content: e.target.value }))} />
                    </div>
                    <div>
                        <label>Ghi chú</label>
                        <Input.TextArea rows={2} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
                    </div>
                </div>
            </Modal>
        </div>
    );
}
